import { z } from 'zod';

/**
 * Domain schemas for health check entities
 * Using Base Schema + Variants pattern for scalability
 */

/**
 * Database health details (from database package)
 */
const DatabaseDetailsSchema = z.object({
  connected: z.boolean(),
  readyState: z.number(),
  error: z.string().optional(),
});

/**
 * Database health status (from database package)
 */
const DatabaseHealthSchema = z.object({
  status: z.enum(['healthy', 'unhealthy']),
  details: DatabaseDetailsSchema,
});

/**
 * Connection status (from database package)
 */
const ConnectionStatusSchema = z.object({
  connected: z.boolean(),
  readyState: z.number(),
  readyStateName: z.string(),
});

/**
 * Base health check schema with all possible fields
 * This serves as the foundation for all health-related responses
 */
const BaseHealthSchema = z.object({
  // Common fields across all health checks
  status: z.enum(['healthy', 'unhealthy', 'ready', 'not-ready', 'alive']),
  timestamp: z.string(),

  // Optional fields that may appear in different contexts
  uptime: z.number().optional(),
  version: z.string().optional(),
  environment: z.string().optional(),
  error: z.string().optional(),

  // Service-specific fields
  database: DatabaseHealthSchema.optional(),
  connection: ConnectionStatusSchema.optional(),
  services: z
    .object({
      database: z.string(),
    })
    .optional(),
});

/**
 * Health check schema variants for different endpoints and contexts
 */
export const HealthSchemas = {
  /**
   * Main health check (/health) - includes all system information
   */
  full: BaseHealthSchema.extend({
    status: z.enum(['healthy', 'unhealthy']),
    uptime: z.number(),
    version: z.string(),
    environment: z.string(),
    database: DatabaseHealthSchema,
    connection: ConnectionStatusSchema,
  }),

  /**
   * Readiness check (/health/ready) - focuses on service readiness
   */
  readiness: BaseHealthSchema.extend({
    status: z.enum(['ready', 'not-ready']),
    services: z.object({
      database: z.string(),
    }),
  }),

  /**
   * Liveness check (/health/live) - minimal check for app running status
   */
  liveness: BaseHealthSchema.extend({
    status: z.literal('alive'),
    uptime: z.number(),
  }),

  /**
   * Error responses (503 status) - used when health checks fail
   */
  error: BaseHealthSchema.extend({
    status: z.enum(['unhealthy', 'not-ready']),
    error: z.string(),
  }),

  /**
   * Common base for all health responses (useful for union types)
   */
  base: BaseHealthSchema,
};

// Export types
export type HealthCheck = z.infer<typeof HealthSchemas.full>;
export type ReadinessCheck = z.infer<typeof HealthSchemas.readiness>;
export type LivenessCheck = z.infer<typeof HealthSchemas.liveness>;
export type HealthError = z.infer<typeof HealthSchemas.error>;
export type BaseHealth = z.infer<typeof HealthSchemas.base>;
